import React, { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthProvider";
import { useWishlist } from "@/context/WishlistProvider";
import useCurrency from "@/hooks/useCurrency";
import { Heart, Image as ImageIcon, Loader2 } from "lucide-react";
import Link from "next/link";

const baseURL = process.env.NEXT_PUBLIC_BASE_URL || "";

const ProductCard = ({ product = {}, index = 0 }) => {
  const { addToWishList, deleteFromWhisList, wishlist } = useWishlist();
  const [isLoading, setIsLoading] = useState(false);
  const {
    name = "Product Name",
    price = 0,
    government = "N/A",
    images = []
  } = product;

  const wishlistData = wishlist;
  const isInWishlist = Array.isArray(wishlistData) && wishlist.some(item => item.id === product.id);
  const { getToken } = useAuth();
  const token = getToken();
  const [imageError, setImageError] = useState(false);
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // If it's already a full URL or starts with /, use it directly
    if (imagePath.startsWith('http') || imagePath.startsWith('/')) {
      return imagePath;
    }
    // Otherwise, prepend base URL
    return `${baseURL}${imagePath}`;
  };
  const imageUrl = images[0]?.image ? getImageUrl(images[0].image) : null;

  const { selectedCurrency, convertPrice } = useCurrency();

  const handleWishlist = async (id) => {
    try {
      setIsLoading(true);
      if (isInWishlist) {
        await deleteFromWhisList(id);
      } else {
        await addToWishList(id);
      }
    } finally {
      setIsLoading(false);
    }
  };

  

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay:   0.1 }}
      whileHover={{ y: -5 }}
      className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100"
    >
      <div className="relative">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => handleWishlist(product.id)}
          disabled={isLoading}
          className="absolute right-2 top-2 bg-white p-1.5 rounded-lg shadow-md"
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
          ) : (
            <Heart
              className={`w-5 h-5 ${isInWishlist
                ? "text-emerald-600 fill-emerald-600"
                : "text-emerald-600"
                }`}
            />
          )}
        </motion.button>
        <Link href={`/Mobile/CategroyProducts/${product.id}`}>
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.1 }}
          >
            {imageError || !imageUrl ? (
              <div className="w-full h-40 bg-gray-100 flex items-center justify-center">
                <ImageIcon className="w-12 h-12 text-gray-400" />
              </div>
            ) : (
              <img
                src={imageUrl}
                alt={name}
                className="w-full h-40 object-cover"
                onError={() => setImageError(true)}
              />
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="p-3 space-y-1"
          >
            <h3 className="font-medium text-gray-800 truncate">{name}</h3>
            <div className="flex flex-col justify-between mt-2">
              <div className="text-emerald-600 font-semibold whitespace-nowrap">
                {convertPrice(price, selectedCurrency.code)} {selectedCurrency.code}
              </div>
              <div className="text-sm text-gray-500 whitespace-nowrap">{government}</div>
            </div>
          </motion.div>
        </Link>
      </div>
    </motion.div>
  );
};

export default ProductCard;